<template>
  <div class="procurement-chat" v-show="show">
    <div class="pc-mask" @click="close"></div>
    <div class="pc-box">
      <!-- 头部 -->
      <div class="pc-header">
        <div class="pc-title">
          <span class="dot"></span>
          在线沟通
          <span class="staff-name" v-if="staffName">（{{ staffName }}）</span>
          <span class="order-no" v-if="orderNo" :title="orderNo" @click.stop="copyOrderNo(orderNo)">订单号：{{ orderNo }}</span>
        </div>
        <div class="pc-close" @click="close">×</div>
      </div>

      <!-- 消息区 -->
      <div class="pc-body" ref="body" @scroll="onScroll">
        <div
          class="load-more"
          v-if="hasMore"
          @click="loadHistory"
        >
          加载更早消息
        </div>
        <div class="pc-empty" v-if="!messageList.length">
          暂无消息，发送第一条消息开始沟通吧
        </div>
        <template v-for="(item, index) in messageList">
          <div
            :key="item.chat_uuid"
            class="msg-item"
            :class="isSelf(item) ? 'self' : 'other'"
          >
            <img
              class="avatar"
              :src="item.sender_avatar | default_img"
              alt=""
            />
            <div class="msg-main">
              <div class="sender-name">{{ item.sender_name }}</div>
              <div class="bubble">
                <!-- 引用 -->
                <div
                  class="quote-box"
                  v-if="item.quote && item.quote.message"
                >
                  <div class="quote-name">{{ item.quote.sender_name }}</div>
                  <div class="quote-text">
                    {{ quotePreview(item.quote) }}
                  </div>
                </div>
                <!-- 撤回 -->
                <div class="recall-text" v-if="item.recall == 1">
                  消息已撤回
                </div>
                <!-- 文本 -->
                <div
                  v-else-if="item.message_type == 'text'"
                  class="msg-text"
                >{{ item.message }}</div>
                <!-- 图片 -->
                <div
                  v-else-if="item.message_type == 'img'"
                  class="msg-img"
                >
                  <img
                    v-for="(f, fi) in item.message_file"
                    :key="fi"
                    :src="f.url"
                    @click="previewImage(item.message_file, fi)"
                    alt=""
                  />
                </div>
                <!-- 文件 -->
                <div
                  v-else-if="item.message_type == 'file'"
                  class="msg-file"
                >
                  <a
                    v-for="(f, fi) in item.message_file"
                    :key="fi"
                    :href="f.url"
                    target="_blank"
                  >
                    <Icon type="ios-document" />
                    <span class="file-name">{{ f.name }}</span>
                  </a>
                </div>
                <!-- 其它类型 -->
                <div v-else class="msg-text">{{ item.message || '[消息]' }}</div>
              </div>
              <div class="msg-meta">
                <span class="time">{{ item.create_time | timeAgo }}</span>
                <a
                  class="recall-link"
                  v-if="canRecall(item)"
                  @click="recall(item)"
                >撤回</a>
                <a
                  class="quote-link"
                  v-if="canQuote(item)"
                  @click="setQuote(item)"
                >引用</a>
              </div>
            </div>
          </div>
        </template>
      </div>

      <!-- 引用条 -->
      <div class="pc-quote" v-if="quoteItem">
        <div class="quote-content">
          <span class="quote-tag">引用</span>
          <span class="quote-text">{{ quotePreview(quoteItem) }}</span>
        </div>
        <span class="quote-cancel" @click="quoteItem = null">×</span>
      </div>

      <!-- 输入区 -->
      <div class="pc-footer">
        <div class="toolbar">
          <label class="tool-btn">
            <Icon type="ios-image-outline" />
            <span>图片</span>
            <input
              type="file"
              accept="image/*"
              multiple
              @change="uploadFile($event, 'img')"
            />
          </label>
          <label class="tool-btn">
            <Icon type="ios-document-outline" />
            <span>文件</span>
            <input
              type="file"
              @change="uploadFile($event, 'file')"
            />
          </label>
        </div>
        <div class="input-row">
          <textarea
            class="pc-input"
            v-model="inputText"
            placeholder="请输入消息..."
            @keydown.enter.exact.prevent="sendText"
          ></textarea>
          <button class="send-btn" :disabled="sending" @click="sendText">
            发送
          </button>
        </div>
      </div>
    </div>

    <!-- 大图预览 -->
    <div class="pc-img-preview" v-if="previewUrl" @click="previewUrl = ''">
      <img :src="previewUrl" alt="" />
    </div>
  </div>
</template>

<script>
import {
  GetProcurementChat,
  GetProcurementChatList,
  SendProcurementChat,
  SetProcurementChatRead,
  RecallProcurementChat,
} from "@/api/procurement.js";
import { uploadFileToOSS } from "@/utils/oss.js";
import { Message, Modal } from "iview";

export default {
  name: "ProcurementChat",
  data() {
    return {
      show: false,
      loading: false,
      sending: false,
      orderUuid: "",
      orderNo: "",
      staffName: "",
      buyerUserId: 0,
      staffEmployeeId: 0,
      messageList: [],
      page: 1,
      pageSize: 30,
      total: 0,
      hasMore: false,
      inputText: "",
      quoteItem: null,
      previewUrl: "",
      currentUserId: 0,
      currentUserAvatar: "",
      currentUserName: "",
      loadMoreLock: false,
    };
  },
  created() {
    const user = this.$store.state.userInfo || {};
    this.currentUserId = user.id || 0;
    this.currentUserAvatar = user.avatar || "";
    this.currentUserName = user.nickname || user.username || "";
    // 订阅推送
    this.$EventBus.$on("procurement-message", this.onPushMessage);
    this.$EventBus.$on("procurement-message-recall", this.onPushRecall);
  },
  beforeDestroy() {
    this.$EventBus.$off("procurement-message", this.onPushMessage);
    this.$EventBus.$off("procurement-message-recall", this.onPushRecall);
    // 确保离开页面时恢复全局通知能力
    this.$store.commit("scoket/setProcurementChatShow", false);
  },
  methods: {
    // 把后端返回的 create_time 统一规范成 int 秒级时间戳
    normalizeTime(m) {
      if (!m || m.create_time == null) return m
      const v = m.create_time
      let ts = 0
      try {
        if (typeof v === 'string') {
          if (/^\d{10,13}$/.test(v)) {
            ts = parseInt(v, 10)
            if (ts > 1e12) ts = Math.floor(ts / 1000)
          } else {
            ts = Math.floor(new Date(v.replace(/-/g, '/')).getTime() / 1000)
          }
        } else if (typeof v === 'number') {
          ts = v > 1e12 ? Math.floor(v / 1000) : v
        } else {
          ts = parseInt(String(v), 10)
        }
      } catch (e) {
        ts = 0
      }
      if (ts && !isNaN(ts)) {
        m.create_time = ts
      } else {
        m.create_time = 0
      }
      return m
    },
    // 打开聊天：orderUuid 为代购订单uuid；orderInfo 含 true_name/avatar/order_no
    async open(orderUuid, orderInfo) {
      this.orderUuid = orderUuid;
      this.orderNo = (orderInfo && orderInfo.order_no) || "";
      this.reset();
      this.show = true;
      // 标记聊天窗口打开，全局 scoket 收到该订单新消息时不再弹通知
      this.$store.commit("scoket/setProcurementChatShow", true);
      this.staffName = (orderInfo && orderInfo.true_name) || "";
      try {
        const { data } = await GetProcurementChat({ order_uuid: orderUuid });
        this.staffName = data.staff_name || this.staffName;
        this.staffEmployeeId = data.staff_employee_id;
        this.buyerUserId = data.buyer_user_id;
      } catch (e) {
        // 会话创建失败时仍允许查看
      }
      await this.loadHistory(true);
      // 标记已读
      SetProcurementChatRead({ order_uuid: orderUuid });
    },
    close() {
      this.show = false;
      // 恢复全局 scoket 通知能力 + 广播未读刷新
      this.$store.commit("scoket/setProcurementChatShow", false);
      this.$EventBus.$emit("procurement-unread-refresh");
    },
    reset() {
      this.messageList = [];
      this.page = 1;
      this.total = 0;
      this.hasMore = false;
      this.inputText = "";
      this.quoteItem = null;
    },
    isSelf(item) {
      return item.sender_type == "user";
    },
    canRecall(item) {
      if (item.recall == 1) return false;
      if (!this.isSelf(item)) return false;
      // 超过10分钟不可撤回
      const now = Math.floor(Date.now() / 1000);
      return now - item.create_time <= 600;
    },
    canQuote(item) {
      return item.recall != 1 && item.message_type == "text";
    },
    setQuote(item) {
      this.quoteItem = item;
    },
    quotePreview(item) {
      if (!item) return "";
      if (item.recall == 1) return "消息已撤回";
      if (item.message_type == "img") return "[图片]";
      if (item.message_type == "file") return "[文件]";
      return item.message || "[消息]";
    },
    async onPushMessage(message) {
      console.log("[ProcurementChat][pc-comp] onPushMessage 收到事件", message);
      // 仅处理当前订单的消息
      if (!message || !message.chat) {
        console.log("[ProcurementChat][pc-comp] 消息缺少 chat 字段，丢弃");
        return;
      }
      if (message.chat.procurement_order_uuid != this.orderUuid) {
        console.log(
          "[ProcurementChat][pc-comp] 订单不匹配，丢弃 当前orderUuid=" +
            this.orderUuid +
            " 消息orderUuid=" +
            message.chat.procurement_order_uuid
        );
        return;
      }
      // 消息已存在则跳过
      if (this.messageList.some((m) => m.chat_uuid == message.chat_uuid)) return;
      this.normalizeTime(message);
      this.messageList.push(message);
      this.total++;
      this.scrollToBottom();
      // 声音/系统通知由全局 scoket 统一处理（避免重复提醒）
      if (this.show) {
        // 代购员正在查看会话，标记已读
        SetProcurementChatRead({ order_uuid: this.orderUuid });
      }
    },
    onPushRecall(chatUuid) {
      const target = this.messageList.find((m) => m.chat_uuid == chatUuid);
      if (target) {
        target.recall = 1;
      }
    },
    async loadHistory(reset = false) {
      if (this.loading) return;
      this.loading = true;
      if (reset) {
        this.page = 1;
        this.hasMore = false;
      }
      try {
        const params = {
          order_uuid: this.orderUuid,
          page: this.page,
          page_size: this.pageSize,
        };
        const { data } = await GetProcurementChatList(params);
        this.total = data.total;
        // 统一规范 create_time 为 int 秒级时间戳
        const list = (data.list || []).map((m) => this.normalizeTime(m));
        if (reset) {
          this.messageList = list;
        } else {
          this.messageList = [...list, ...this.messageList];
        }
        this.hasMore = this.messageList.length < this.total;
        this.page++;
      } catch (e) {
        // ignore
      } finally {
        this.loading = false;
      }
      this.$nextTick(() => {
        if (reset) {
          this.scrollToBottom(false);
        } else {
          // 加载更早，保持顶部位置
          const body = this.$refs.body;
          if (body) body.scrollTop = body.scrollHeight - this.prevScrollHeight;
        }
      });
    },
    onScroll(e) {
      const body = this.$refs.body;
      if (!body) return;
      // 顶部触发加载更早
      if (body.scrollTop < 30 && this.hasMore && !this.loading) {
        this.prevScrollHeight = body.scrollHeight;
        this.loadHistory();
      }
    },
    scrollToBottom(smooth = true) {
      this.$nextTick(() => {
        const body = this.$refs.body;
        if (body) body.scrollTop = body.scrollHeight;
      });
    },
    async sendText() {
      const text = this.inputText.trim();
      if (!text || this.sending) return;
      this.sending = true;
      try {
        const params = {
          order_uuid: this.orderUuid,
          message_type: "text",
          message: text,
        };
        if (this.quoteItem) {
          params.quote = this.quoteItem;
        }
        const { data } = await SendProcurementChat(params);
        this.normalizeTime(data);
        this.messageList.push(data);
        this.total++;
        this.inputText = "";
        this.quoteItem = null;
        this.scrollToBottom();
      } catch (e) {
        Message.error("发送失败");
      } finally {
        this.sending = false;
      }
    },
    async uploadFile(event, fileType) {
      const files = Array.from(event.target.files || []);
      event.target.value = "";
      for (const file of files) {
        try {
          const url = await uploadFileToOSS(file, "procurement_chat");
          const params = {
            order_uuid: this.orderUuid,
            message_type: fileType,
            message: "",
            message_file: [{ type: fileType, url, name: file.name }],
          };
          const { data } = await SendProcurementChat(params);
          this.normalizeTime(data);
          this.messageList.push(data);
          this.total++;
          this.scrollToBottom();
        } catch (e) {
          Message.error("文件上传失败");
        }
      }
    },
    async recall(item) {
      const confirmed = await new Promise((resolve) => {
        Modal.confirm({
          title: "撤回消息",
          content: "确定撤回这条消息吗？",
          onOk: () => resolve(true),
          onCancel: () => resolve(false),
        });
      });
      if (!confirmed) return;
      try {
        await RecallProcurementChat({ chat_uuid: item.chat_uuid });
        item.recall = 1;
      } catch (e) {
        Message.error("撤回失败");
      }
    },
    previewImage(list, index) {
      // 大图预览：使用轻量遮罩层（避免 window.open 被弹窗拦截）
      const url = list[index] && list[index].url;
      if (!url) return;
      this.previewUrl = url;
    },
    // 复制订单号（clipboard API + 兜底）
    async copyOrderNo(orderNo) {
      if (!orderNo) return;
      const fallback = () => {
        const ta = document.createElement("textarea");
        ta.value = orderNo;
        ta.style.position = "fixed";
        ta.style.opacity = "0";
        document.body.appendChild(ta);
        ta.select();
        try {
          document.execCommand("copy");
          this.$layer.msg("复制成功");
        } catch (e) {
          Message.error("复制失败，请手动选择");
        }
        document.body.removeChild(ta);
      };
      try {
        if (navigator.clipboard && window.isSecureContext) {
          await navigator.clipboard.writeText(orderNo);
          this.$layer.msg("复制成功");
        } else {
          fallback();
        }
      } catch (e) {
        fallback();
      }
    },
  },
  filters: {
    timeAgo(v) {
      // 1) 空值 / 0 / 非法值直接兜底
      if (v === '' || v == null || v === 0) return "";
      // 2) 解析出秒级时间戳，覆盖字符串(YYYY-MM-DD HH:MM:SS)、字符串时间戳、数字时间戳
      let ts = 0;
      try {
        if (typeof v === 'string') {
          // 可能为 '2026-08-20 23:40:39' 或 '1723456789'
          if (/^\d{10,13}$/.test(v)) {
            ts = parseInt(v, 10);
            if (ts > 1e12) ts = Math.floor(ts / 1000);
          } else {
            ts = Math.floor(new Date(v.replace(/-/g, '/')).getTime() / 1000);
          }
        } else if (typeof v === 'number') {
          ts = v > 1e12 ? Math.floor(v / 1000) : v;
        } else if (v instanceof Date) {
          ts = Math.floor(v.getTime() / 1000);
        } else {
          ts = parseInt(String(v), 10);
        }
      } catch (e) {
        return "";
      }
      if (!ts || isNaN(ts)) return "";
      const d = new Date(ts * 1000);
      const now = Date.now();
      const diff = Math.floor((now - ts * 1000) / 60000);
      if (diff < 1) return "刚刚";
      if (diff < 60) return diff + "分钟前";
      const h = Math.floor(diff / 60);
      if (h < 24) return h + "小时前";
      const mm = `${d.getMonth() + 1}`.padStart(2, "0");
      const dd = `${d.getDate()}`.padStart(2, "0");
      return `${d.getFullYear()}-${mm}-${dd}`;
    },
  },
};
</script>

<style scoped lang="scss">
.procurement-chat {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: 3000;
  .pc-mask {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: rgba(0, 0, 0, 0.45);
  }
  .pc-img-preview {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: rgba(0, 0, 0, 0.75);
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: zoom-out;
    img {
      max-width: 90%;
      max-height: 90%;
      border-radius: 4px;
    }
  }
  .pc-box {
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    width: 720px;
    height: 560px;
    background: #fff;
    border-radius: 8px;
    display: flex;
    flex-direction: column;
    overflow: hidden;
    box-shadow: 0 8px 32px rgba(0, 0, 0, 0.2);
  }
  .pc-header {
    height: 52px;
    padding: 0 16px;
    background: linear-gradient(135deg, #2d8cf0, #1b6fd0);
    color: #fff;
    display: flex;
    align-items: center;
    justify-content: space-between;
    .pc-title {
      display: flex;
      align-items: center;
      font-size: 16px;
      .dot {
        width: 8px;
        height: 8px;
        border-radius: 50%;
        background: #52c41a;
        margin-right: 8px;
      }
      .staff-name {
        font-size: 13px;
        opacity: 0.9;
        margin-left: 4px;
      }
      .order-no {
        font-size: 12px;
        margin-left: 12px;
        padding: 2px 8px;
        background: rgba(255, 255, 255, 0.18);
        border-radius: 10px;
        cursor: pointer;
        max-width: 240px;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
        transition: background 0.15s;
        &:hover {
          background: rgba(255, 255, 255, 0.32);
        }
      }
    }
    .pc-close {
      font-size: 26px;
      cursor: pointer;
      line-height: 1;
      &:hover {
        opacity: 0.7;
      }
    }
  }
  .pc-body {
    flex: 1;
    overflow-y: auto;
    padding: 16px;
    background: #f5f7fb;
    .load-more {
      text-align: center;
      color: #999;
      font-size: 12px;
      padding: 8px 0;
      cursor: pointer;
      &:hover {
        color: #2d8cf0;
      }
    }
    .pc-empty {
      text-align: center;
      color: #aaa;
      padding-top: 80px;
      font-size: 14px;
    }
    .msg-item {
      display: flex;
      margin-bottom: 16px;
      .avatar {
        width: 40px;
        height: 40px;
        border-radius: 50%;
        flex-shrink: 0;
      }
      .msg-main {
        margin: 0 10px;
        max-width: 70%;
        min-width: 0;
        width: fit-content;
        // 让所有子元素自然收缩不撑满父容器
        display: flex;
        flex-direction: column;
        align-items: flex-start; // other：左；self 下覆盖为 flex-end
        .sender-name {
          font-size: 12px;
          color: #999;
          margin-bottom: 4px;
          width: fit-content;
        }
        .bubble {
          background: #fff;
          border-radius: 4px;
          padding: 10px 12px;
          border: 1px solid #e8eaef;
          display: inline-block;
          width: auto;
          max-width: 100%;
          .quote-box {
            border-left: 3px solid #2d8cf0;
            padding: 4px 8px;
            background: #f2f5fb;
            margin-bottom: 6px;
            border-radius: 2px;
            .quote-name {
              font-size: 12px;
              color: #2d8cf0;
            }
            .quote-text {
              font-size: 12px;
              color: #666;
              max-width: 360px;
              overflow: hidden;
              text-overflow: ellipsis;
              white-space: nowrap;
            }
          }
          .recall-text {
            color: #999;
            font-size: 13px;
          }
          .msg-text {
            font-size: 14px;
            color: #333;
            word-break: break-word;
            white-space: pre-wrap;
          }
          .msg-img {
            img {
              max-width: 180px;
              max-height: 180px;
              border-radius: 4px;
              margin-right: 4px;
              cursor: pointer;
            }
          }
          .msg-file {
            a {
              display: flex;
              align-items: center;
              color: #2d8cf0;
              font-size: 14px;
              .file-name {
                margin-left: 4px;
                max-width: 200px;
                overflow: hidden;
                text-overflow: ellipsis;
                white-space: nowrap;
              }
            }
          }
        }
        .msg-meta {
          margin-top: 4px;
          font-size: 12px;
          color: #999;
          width: fit-content;
          .time {
            margin-right: 8px;
          }
          .recall-link,
          .quote-link {
            margin-right: 8px;
            cursor: pointer;
            &:hover {
              color: #2d8cf0;
            }
          }
        }
      }
      &.self {
        flex-direction: row-reverse;
        .msg-main {
          align-items: flex-end; // self：列内容右对齐（flex column 下）
          text-align: right;
          .sender-name {
            text-align: right;
          }
          .bubble {
            background: #2d8cf0;
            color: #fff;
            border: none;
            .quote-box {
              background: rgba(255, 255, 255, 0.2);
              .quote-name {
                color: #fff;
              }
              .quote-text {
                color: #eee;
              }
            }
            .msg-text {
              color: #fff;
            }
            .msg-file a {
              color: #fff;
            }
          }
          .msg-meta {
            text-align: right;
          }
        }
      }
    }
  }
  .pc-quote {
    padding: 8px 16px;
    border-top: 1px solid #eee;
    display: flex;
    align-items: center;
    justify-content: space-between;
    background: #fafbfc;
    .quote-content {
      display: flex;
      align-items: center;
      min-width: 0;
      .quote-tag {
        background: #2d8cf0;
        color: #fff;
        font-size: 12px;
        border-radius: 2px;
        padding: 1px 4px;
        margin-right: 6px;
        flex-shrink: 0;
      }
      .quote-text {
        font-size: 13px;
        color: #666;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
      }
    }
    .quote-cancel {
      cursor: pointer;
      color: #999;
      font-size: 16px;
      flex-shrink: 0;
      margin-left: 8px;
      &:hover {
        color: #333;
      }
    }
  }
  .pc-footer {
    border-top: 1px solid #eee;
    padding: 10px 16px 12px;
    background: #fff;
    .toolbar {
      display: flex;
      margin-bottom: 8px;
      .tool-btn {
        display: flex;
        align-items: center;
        cursor: pointer;
        color: #666;
        margin-right: 16px;
        font-size: 13px;
        input {
          display: none;
        }
        &:hover {
          color: #2d8cf0;
        }
      }
    }
    .input-row {
      display: flex;
      align-items: flex-end;
      .pc-input {
        flex: 1;
        height: 70px;
        border: 1px solid #e0e0e0;
        border-radius: 4px;
        padding: 8px 10px;
        resize: none;
        font-size: 14px;
        outline: none;
        &:focus {
          border-color: #2d8cf0;
        }
      }
      .send-btn {
        margin-left: 10px;
        width: 70px;
        height: 36px;
        background: #2d8cf0;
        color: #fff;
        border: none;
        border-radius: 4px;
        cursor: pointer;
        &:hover {
          opacity: 0.85;
        }
        &:disabled {
          opacity: 0.5;
          cursor: not-allowed;
        }
      }
    }
  }
}
</style>
